import http from "@/services/http";
import { parseDictation } from "@/features/patients/prescriptions/utils/parseDictation.js";

/**
 * Lógica compartilhada entre PrescriptionDialog e ExamAddDialog para
 * transformar o ditado por voz (speechToText) em itens estruturados
 * salvos no banco (selectedItems → PrescriptionItem).
 *
 * Espera que o componente host forneça:
 *   - data:   selectedItems (array)
 *   - refs:   editor (contenteditable), dictation (DictationButton)
 *   - métodos: removeItem(position), updateContent(), onDictationToggle(bool),
 *              insertDictatedText(text)  ← usado como fallback
 *   - (opcional) data prescriptionType — se ausente, assume 'exam'
 */
export default {
  data() {
    return {
      dictationBuffer: "",
      dictationParsing: false,
      showDictationReview: false,
      parsedDictationItems: [],
    };
  },

  computed: {
    // PrescriptionDialog tem prescriptionType reativo; ExamAddDialog é sempre 'exam'.
    dictationType() {
      return this.prescriptionType || "exam";
    },
  },

  methods: {
    // Acumula cada frase final reconhecida numa nova linha. Como a Web Speech
    // API fecha um "final" a cada pausa da fala, cada pausa vira um item
    // separado (além das palavras-gatilho "próximo"/"depois").
    onDictationTranscript(text) {
      if (!text) return;
      this.dictationBuffer += (this.dictationBuffer ? "\n" : "") + text.trim();
    },

    // Chamado a cada mudança de gravação do DictationButton.
    onDictationRecordingChange(isRecording) {
      // Mantém o comportamento de foco do editor já existente no host.
      if (typeof this.onDictationToggle === "function") {
        this.onDictationToggle(isRecording);
      }
      // Ao parar, processa o que foi ditado.
      if (!isRecording) this.finishDictation();
    },

    async finishDictation() {
      const buffer = this.dictationBuffer.trim();
      this.dictationBuffer = "";
      if (!buffer) return;

      const type = this.dictationType;
      const parsed = parseDictation(buffer, type);

      // Não conseguiu estruturar → insere texto solto (comportamento antigo).
      if (!parsed.length) {
        if (typeof this.insertDictatedText === "function") {
          this.insertDictatedText(buffer);
        }
        return;
      }

      this.dictationParsing = true;
      try {
        // Casa cada nome contra a base TUSS (medicamento/exame). Suplemento não tem API.
        for (const item of parsed) {
          await this.matchDictationItem(item, type);
        }
      } finally {
        this.dictationParsing = false;
      }

      this.parsedDictationItems = parsed;
      this.showDictationReview = true;
    },

    async matchDictationItem(item, type) {
      if (!item.name || item.name.length < 2) return;

      const endpoint =
        type === "exam"
          ? "/api/prescription/search-exams"
          : type === "supplement"
            ? "/api/prescription/search-supplement"
            : "/api/prescription/search-medication";

      try {
        const res = await http.post(
          endpoint,
          { texto: item.name },
          { headers: { "Content-Type": "application/json" } },
        );
        const cand = (res.data?.candidatos || [])[0];
        if (!cand) return;

        if (type === "exam") {
          item.matchName = cand.descricao_encontrada;
          item.tuss_code = cand.codigo_tuss;
          item.score = cand.similaridade;
        } else if (type === "supplement") {
          item.matchName = cand.nome;
          item.tuss_code = cand.id_tuss;
          item.score = cand.score;
        } else {
          item.matchName = cand.nome;
          item.tuss_code = cand.id_tuss;
          item.score = cand.score;
          item.presentation = cand.apresentacao || "";
          if (!item.dosage && cand.dosagem) item.dosage = cand.dosagem;
        }
      } catch (e) {
        // Sem match → segue com o que foi ditado (vira texto no full_content).
        console.warn("Falha ao casar item ditado:", item.name, e);
      }
    },

    // Confirmação do dialog de revisão: vira selectedItems + chips no editor.
    confirmDictationItems(items) {
      for (const it of items) {
        // Monta a quantidade a partir de número + unidade, igual ao fluxo "@".
        const quantidade = it.quantidade_num
          ? `${it.quantidade_num} ${it.quantidade_unidade || ""}`.trim()
          : "";
        const structured = {
          name: it.name,
          type: it.type,
          tuss_code: it.tuss_code || null,
          dosage: it.dosage || "",
          presentation: it.presentation || "",
          quantidade,
          frequencia: it.frequencia || "",
          duracao: it.duracao || "",
          via_administracao: it.via_administracao || "",
          observacoes: it.observacoes || "",
          score: it.score || null,
          condition_ids: Array.isArray(it.condition_ids)
            ? it.condition_ids
            : [],
          position: this.selectedItems.length,
          timestamp: new Date().toISOString(),
        };
        this.selectedItems.push(structured);
        this.appendStructuredItemToEditor(structured);
      }
      this.showDictationReview = false;
      this.parsedDictationItems = [];
      this.$nextTick(() => this.updateContent());
    },

    cancelDictationReview() {
      this.showDictationReview = false;
      this.parsedDictationItems = [];
    },

    // Insere o chip estruturado no fim do editor (não depende de savedRange,
    // diferente do insertItemInEditor do fluxo "@").
    appendStructuredItemToEditor(item) {
      const editor = this.$refs.editor;
      if (!editor) return;

      let itemText = item.name;
      if (item.type === "medication" || item.type === "supplement") {
        if (item.dosage) itemText += ` - ${item.dosage}`;
        if (item.quantidade) itemText += ` - ${item.quantidade}`;
        if (item.frequencia) itemText += ` - ${item.frequencia}`;
        if (item.duracao) itemText += ` - ${item.duracao}`;
        if (item.observacoes) itemText += ` (${item.observacoes})`;
      }

      const wrapper = document.createElement("div");
      wrapper.className =
        item.type === "medication"
          ? "medication-line"
          : item.type === "supplement"
            ? "supplement-line"
            : "exam-line";
      wrapper.setAttribute("data-tuss", item.tuss_code || "");
      wrapper.setAttribute("data-item-index", item.position);
      wrapper.setAttribute("data-item-type", item.type);
      wrapper.style.display = "flex";
      wrapper.style.alignItems = "center";
      wrapper.style.justifyContent = "space-between";
      wrapper.style.gap = "8px";

      const textSpan = document.createElement("span");
      textSpan.style.flex = "1";
      textSpan.textContent = itemText;

      const deleteBtn = document.createElement("button");
      deleteBtn.innerHTML = "×";
      deleteBtn.type = "button";
      deleteBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeItem(item.position);
      };

      wrapper.appendChild(textSpan);
      wrapper.appendChild(deleteBtn);
      editor.appendChild(wrapper);
      editor.appendChild(document.createElement("br"));
    },
  },
};
